import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Icon, Label, Menu } from "semantic-ui-react";
import { useGetHitsQuery, useMarkHitsReadMutation } from "../features/api/apiSlice";

// Top-bar bell. Shows the user's recent notification history (fetched once) plus
// any that arrive live over the shared socket while the page is open, with an
// unread badge. Opening the dropdown marks everything read.
//
// `socket` is the same socket.io connection Calls.js already holds; we just add
// a 'notification' listener to it.
export default function NotificationBell({ socket }) {
  const { data } = useGetHitsQuery();
  const [markRead] = useMarkHitsReadMutation();

  // Live hits arriving over the socket, newest first, kept separate from the
  // fetched history so we don't have to refetch on every push.
  const [liveHits, setLiveHits] = useState([]);
  const [liveUnread, setLiveUnread] = useState(0);

  useEffect(() => {
    if (!socket) return undefined;
    const onNotification = (raw) => {
      let hit;
      try {
        hit = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch (e) {
        return;
      }
      setLiveHits((prev) => [hit, ...prev].slice(0, 50));
      setLiveUnread((n) => n + 1);
    };
    socket.on("notification", onNotification);
    return () => socket.off("notification", onNotification);
  }, [socket]);

  const fetchedHits = (data && data.hits) || [];
  const fetchedUnread = (data && data.unread) || 0;

  const hits = [...liveHits, ...fetchedHits].slice(0, 50);
  const unread = fetchedUnread + liveUnread;

  const handleOpen = () => {
    if (unread > 0) {
      markRead();
      setLiveUnread(0);
    }
  };

  const describe = (h) => {
    const who = h.alias && h.alias !== "N/A" ? `${h.alias} (${h.rid})` : `RID ${h.rid}`;
    return `${who} on TG ${h.talkgroupNum}`;
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Deep link to the call, shaped exactly like the shareable Link button
  // (CallLinks.js): call-id picks the call, and time (the call's time + 1ms)
  // makes Calls.js load the archive page that actually contains it — without
  // it the frontend fetches the latest page and an older call never plays.
  // Live socket hits carry `time`, stored hits `callTime`; hits recorded
  // before callTime existed fall back to when the hit fired, which is within
  // moments of the call.
  const callHref = (h) => {
    if (!h.callId) return undefined;
    const ts = h.callTime || h.time || h.createdAt;
    let href = `/system/${h.shortName}?call-id=${h.callId}`;
    if (ts) {
      href += `&time=${new Date(ts).getTime() + 1}`;
    }
    return href;
  };

  const trigger = (
    <span>
      <Icon name="bell" />
      {unread > 0 && (
        <Label color="red" floating circular size="tiny">
          {unread > 99 ? "99+" : unread}
        </Label>
      )}
    </span>
  );

  return (
    <Dropdown item icon={null} trigger={trigger} onOpen={handleOpen} pointing="top right">
      <Dropdown.Menu style={{ minWidth: "20em", maxHeight: "24em", overflowY: "auto", zIndex: 1000 }}>
        <Dropdown.Header content="Notifications" />
        {hits.length === 0 ? (
          <Dropdown.Item disabled text="No notifications yet" />
        ) : (
          hits.map((h, i) => (
            <Dropdown.Item
              key={h.id || h._id || i}
              as={h.callId ? "a" : "div"}
              // Full-page navigation (href, not react-router Link) to the call's
              // deep link. Calls.js reads the call and plays it in setStateFromUri,
              // which only runs on mount — an in-app Link wouldn't re-trigger it.
              href={callHref(h)}
            >
              <div style={{ fontWeight: 600 }}>{describe(h)}</div>
              <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
                {h.shortName}
                {h.label ? ` · ${h.label}` : ""} · {timeAgo(h.createdAt || h.time)}
              </div>
            </Dropdown.Item>
          ))
        )}
        <Dropdown.Divider />
        <Dropdown.Item as={Link} to="/notifications">
          <Icon name="cog" /> Manage notifications
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
